import { Box, CircularProgress } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FONT_MONO } from "../../theme/v2Tokens";
import {
  Transcription,
  TranscriptionReader,
  TranscriptionBlock,
} from "../../../types/transcription/Transcription";
import { useTranscriptRequest } from "../../../hooks/useTranscription";
import { useTranscribeAndSummarize } from "../../../hooks/useSummary";
import { archieButtonSx, archieIconSvg } from "./SummaryTab";

interface TranscriptTabProps {
  customerId: string;
  interactionId: string;
  interactionType: string;
  hasMedia: boolean;
  transcript?: Transcription;
  isLoading: boolean;
  currentTimeSeconds?: number;
}

function formatBlockTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function TranscriptTab({
  customerId,
  interactionId,
  interactionType,
  hasMedia,
  transcript,
  isLoading,
  currentTimeSeconds = 0,
}: TranscriptTabProps) {
  const { t } = useTranslation("common");
  const [requestSent, setRequestSent] = useState(false);

  const transcriptRequest = useTranscriptRequest();
  const transcribeAndSummarize = useTranscribeAndSummarize();

  const blocks: TranscriptionBlock[] = useMemo(() => {
    if (!transcript) return [];
    try {
      const reader = new TranscriptionReader(transcript);
      return reader.blocks;
    } catch {
      return [];
    }
  }, [transcript]);

  const canTranscribe =
    ["Audio", "Video", "AudioAndVideo"].includes(interactionType) && hasMedia;

  // Determine active block based on current playback time
  const activeBlockId = useMemo(() => {
    if (!blocks.length || currentTimeSeconds <= 0) return -1;
    for (let i = blocks.length - 1; i >= 0; i--) {
      if (blocks[i].startSeconds <= currentTimeSeconds) {
        return blocks[i].id;
      }
    }
    return -1;
  }, [blocks, currentTimeSeconds]);

  // Auto-scroll to the active block
  const activeBlockRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (activeBlockRef.current) {
      activeBlockRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeBlockId]);

  const handleTranscribe = () => {
    setRequestSent(true);
    transcriptRequest.mutate({ customerId, interactionId });
  };

  const handleTranscribeAndSummarize = () => {
    setRequestSent(true);
    transcribeAndSummarize.mutate({ customerId, interactionId });
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 120,
          gap: 1,
        }}
      >
        <CircularProgress size={16} />
        <Box
          component="span"
          sx={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}
        >
          {t("loadingTranscript")}
        </Box>
      </Box>
    );
  }

  // No transcript — show action buttons
  if (blocks.length === 0) {
    const isPending =
      requestSent ||
      transcriptRequest.isPending ||
      transcribeAndSummarize.isPending;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          py: 4,
          px: 2,
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: "0.8125rem",
            color: "var(--text-tertiary)",
            mb: 1,
          }}
        >
          {t("noTranscriptAvailable")}
        </Box>

        {canTranscribe && !isPending && (
          <>
            <Box
              component="button"
              onClick={handleTranscribe}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: "#fff",
                backgroundColor: "var(--accent)",
                border: "none",
                borderRadius: "8px",
                px: 2.5,
                py: 1,
                cursor: "pointer",
                fontFamily: "inherit",
                "&:hover": { opacity: 0.88 },
              }}
            >
              Transcribe
            </Box>
            <Box component="button" onClick={handleTranscribeAndSummarize} sx={archieButtonSx}>
              {archieIconSvg}
              Transcribe & Summarize with Archie
            </Box>
          </>
        )}

        {isPending && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={14} />
            <Box
              component="span"
              sx={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}
            >
              Transcription requested...
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  // Transcript blocks
  return (
    <Box sx={{ py: 1 }}>
      {blocks.map((block) => {
        const isActive = block.id === activeBlockId;
        return (
          <Box
            key={block.id}
            ref={isActive ? activeBlockRef : undefined}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              px: 2,
              py: 0.875,
              borderBottom: "1px solid var(--border-subtle)",
              "&:last-child": { borderBottom: "none" },
              backgroundColor: isActive ? "var(--accent-dim)" : "transparent",
              borderRadius: isActive ? "4px" : 0,
              transition: "background-color 200ms ease",
            }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: FONT_MONO,
                fontSize: "0.6875rem",
                color: isActive ? "var(--accent)" : "var(--text-tertiary)",
                flexShrink: 0,
                minWidth: 32,
                mt: "2px",
              }}
            >
              {formatBlockTime(block.startSeconds)}
            </Box>

            <Box
              component="span"
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 600,
                color:
                  block.speakerLabel === "Agent"
                    ? "var(--signal-blue)"
                    : "var(--signal-amber)",
                flexShrink: 0,
                minWidth: 60,
                mt: "2px",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {block.speakerLabel}
            </Box>

            <Box
              component="span"
              sx={{
                fontSize: "0.8125rem",
                color: isActive ? "var(--text-primary)" : "var(--text-secondary)",
                lineHeight: 1.5,
                fontWeight: isActive ? 500 : 400,
              }}
            >
              {block.text}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
